// src/lib/useNotificationStream.ts — SSE hook for real-time notifications
import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { getApiBaseUrl } from "./apiBase";

const API_URL = getApiBaseUrl();
const RETRY_DELAY = 5_000;

/** Parse SSE text chunks into JSON data payloads */
function parseSseChunk(chunk: string): unknown[] {
  const results: unknown[] = [];
  for (const line of chunk.split("\n")) {
    const trimmed = line.trim();
    if (trimmed.startsWith("data: ")) {
      try {
        results.push(JSON.parse(trimmed.slice(6)));
      } catch { /* ignore malformed */ }
    }
  }
  return results;
}

function playAlertSound() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 660;
    gain.gain.value = 0.1;
    osc.start();
    setTimeout(() => { osc.stop(); ctx.close(); }, 200);
  } catch { /* ignore */ }
}

function tryDesktopNotification(title: string, body?: string | null) {
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(title, { body: body || undefined, icon: "/logo192.png" });
    }
  } catch { /* ignore */ }
}

export function useNotificationStream() {
  const { accessToken, isAuthenticated } = useAuth();
  const qc = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);
  const retryRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mountedRef = useRef(true);

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["notifications-unread"] });
    qc.invalidateQueries({ queryKey: ["notifications-summary"] });
    qc.invalidateQueries({ queryKey: ["notifications-list"] });
    qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
  }, [qc]);

  const connect = useCallback(async () => {
    if (!accessToken || !mountedRef.current) return;

    // Abort previous connection
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API_URL}/v1/notifications/stream`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });

      if (!res.ok || !res.body) return;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      while (mountedRef.current) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const payloads = parseSseChunk(chunk);

        for (const payload of payloads) {
          const p = payload as { type?: string; notification?: { severity?: string; title?: string; body?: string | null; id?: number } };
          if (p.type === "notification") {
            invalidate();

            // Sound + desktop notification for critical alerts
            if (p.notification?.severity === "error") {
              playAlertSound();
              tryDesktopNotification(p.notification.title || "", p.notification.body);
            }
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
    }

    // Retry on disconnect
    if (mountedRef.current) {
      retryRef.current = setTimeout(connect, RETRY_DELAY);
    }
  }, [accessToken, invalidate]);

  useEffect(() => {
    mountedRef.current = true;

    if (!isAuthenticated) return;

    connect();

    // Request desktop notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      clearTimeout(retryRef.current);
    };
  }, [isAuthenticated, connect]);
}
